import { useEffect } from 'react';
import { useAgentStore } from '../../../store/useAgentStore.js';
import { setRunMessages } from '../../../lib/agentRunStore.js';
import { sessionRoot } from '../../../lib/sessionRoot.js';
import { makeGitCheckpoint } from '../../../lib/checkpoint/gitCheckpoint.js';
import { runMatchedHooks, EXECUTOR_EVENT_TO_HOOK } from '../../../lib/hooks/hookRegistry.js';
import { adaptTasksForExecutor } from '../../../lib/tasks/adaptTasks.js';
import { getPendingPlan } from '../../../lib/planStateStore.js';

/**
 * Wires the three executor-related event listeners that were mounted in
 * SessionWorkspace: approveDag, restoreCheckpoint, taskGraph hooks.
 * Each is cross-session guarded and best-effort.
 *
 * @param {{ session: object, handleSendRef: { current: Function }, handlePlanApprove: Function }} opts
 */
export function useExecutorEvents({ session, handleSendRef, handlePlanApprove }) {
  const sessionId = session?.id;

  useEffect(() => {
    function onApproveDag(evt) {
      const sid = evt?.detail?.sessionId;
      if (sid && sid !== sessionId) return;
      useAgentStore.getState().approveCodeTasks?.(sessionId);
      if (getPendingPlan(sessionId)) {
        handlePlanApprove();
        return;
      }
      const storedTasks = useAgentStore.getState().code?.tasks?.tasks || [];
      const dagTasks = adaptTasksForExecutor(storedTasks);
      // AUDIT 2026-07-02 (stale-closure): via handleSendRef.current — the current-render
      // closure (adapters loaded, current messages), not the mount snapshot.
      handleSendRef.current?.({
        text: 'Plano aprovado. Execute as tasks do DAG na ordem topológica (respeitando depends_on), uma de cada vez, verificando cada uma antes de seguir.',
        model: session?.model,
        permissions_mode: session?.permissions_mode,
        plan_mode: false,
        dagTasks,
        executeApproved: true, // approveDag event → EXECUTE the approved DAG, not re-propose
      })?.catch?.(() => {});
    }
    window.addEventListener('apolo:approveDag', onApproveDag);
    return () => window.removeEventListener('apolo:approveDag', onApproveDag);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, session?.model, session?.permissions_mode, handlePlanApprove]);

  useEffect(() => {
    async function onRestoreCheckpoint(evt) {
      const sid = evt?.detail?.sessionId;
      if (sid && sid !== sessionId) return;
      const id = evt?.detail?.checkpoint?.id;
      if (!id) return;
      try {
        const r = sessionRoot(session);
        const runCommand = (p) => window.apolo.shell.exec({ ...p, cwd: p.cwd || r });
        const ok = await makeGitCheckpoint({ root: r, runCommand }).restore(id);
        const msgId = `local-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
        setRunMessages(sessionId, (m) => [...m, {
          id: msgId,
          created_at: new Date().toISOString(),
          role: 'system',
          content: ok ? `↩️ Checkpoint restaurado (${id.slice(0, 8)}).` : `⚠️ Não consegui restaurar o checkpoint ${id.slice(0, 8)}.`,
        }]);
      } catch { /* best-effort */ }
    }
    window.addEventListener('apolo:restoreCheckpoint', onRestoreCheckpoint);
    return () => window.removeEventListener('apolo:restoreCheckpoint', onRestoreCheckpoint);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  useEffect(() => {
    function onTaskGraphForHooks(evt) {
      const d = evt?.detail;
      if (!d || (d.sessionId && d.sessionId !== sessionId)) return;
      const hookEvent = EXECUTOR_EVENT_TO_HOOK[d.type];
      if (!hookEvent) return;
      let hooks = [];
      try { hooks = JSON.parse(window.localStorage.getItem('apolo_local_hooks') || '[]'); } catch { hooks = []; }
      if (!Array.isArray(hooks) || hooks.length === 0) return;
      const r = sessionRoot(session);
      const runCommand = (p) => window.apolo.shell.exec({ ...p, cwd: p.cwd || r });
      runMatchedHooks(hooks, { type: hookEvent, taskId: d.taskId }, { runCommand, root: r }).catch(() => {});
    }
    window.addEventListener('apolo:taskGraph', onTaskGraphForHooks);
    return () => window.removeEventListener('apolo:taskGraph', onTaskGraphForHooks);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);
}
